import { useEffect, useRef, useState } from "react";
import axios from "axios";
import { useTranslation } from "react-i18next";
import { Moon, Sparkles, Download, MessageCircle, Facebook, Twitter, Link2, Calendar, Volume2, Pause } from "lucide-react";
import { toast } from "sonner";
import { absoluteUrl, adminHeaders } from "../lib/api";
import { T } from "@/locales/tr";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Ramadan 1446H ≈ Feb 28 → Mar 30, 2025 ; Ramadan 1447H ≈ Feb 17 → Mar 18, 2026.
// On stocke la date de début pour calculer le jour courant.
const RAMADAN_START_2026 = new Date("2026-02-17T00:00:00Z");

function computeCurrentDay() {
  const now = new Date();
  const diffMs = now.getTime() - RAMADAN_START_2026.getTime();
  const days = Math.floor(diffMs / (1000 * 60 * 60 * 24)) + 1;
  if (days < 1) return null; // pas encore commencé
  if (days > 30) return null; // déjà fini
  return days;
}

const DECADES = [
  { range: [1, 10], key: "decade1", color: "from-emerald-50 to-emerald-100" },
  { range: [11, 20], key: "decade2", color: "from-amber-50 to-amber-100" },
  { range: [21, 30], key: "decade3", color: "from-rose-50 to-rose-100" },
];

export default function Ramadan() {
  const { t } = useTranslation();
  const [days, setDays] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [audioState, setAudioState] = useState("idle"); // idle|loading|playing
  const audioRef = useRef(null);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await axios.get(`${API}/ramadan/calendar`);
        setDays(data.days || []);
        const current = computeCurrentDay();
        const initial = current && data.days.find((d) => d.day === current);
        setSelected(initial || (data.days && data.days[0]));
      } catch (e) {
        toast.error(T("Calendrier indisponible pour le moment."));
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const currentDay = computeCurrentDay();
  const imageUrl = selected ? absoluteUrl(`/api/ramadan/image/${selected.day}`) : "";
  const shareText = selected
    ? `${T("Jour")} ${selected.day} — ${selected.theme}\n« ${selected.text} »\n— ${T("Sourate")} ${selected.sourate_num} (${selected.sourate_name_ar_translit}) v.${selected.verset_num}`
    : "";
  const pageUrl = "https://papicoo.com/ramadan";

  const share = (kind) => {
    if (!selected) return;
    const map = {
      whatsapp: `https://wa.me/?text=${encodeURIComponent(shareText + "\n" + pageUrl)}`,
      facebook: `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(pageUrl)}&quote=${encodeURIComponent(shareText)}`,
      twitter: `https://twitter.com/intent/tweet?text=${encodeURIComponent(shareText)}&url=${encodeURIComponent(pageUrl)}`,
    };
    window.open(map[kind], "_blank", "noopener,noreferrer");
  };

  const copyLink = async () => {
    try { await navigator.clipboard.writeText(`${shareText}\n${pageUrl}`); toast.success(T("Copié 🌹")); }
    catch { toast.error(T("Copie impossible.")); }
  };

  const download = async () => {
    if (!imageUrl) return;
    try {
      const r = await fetch(imageUrl);
      const blob = await r.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob);
      a.download = `papicoo-ramadan-jour-${selected.day}.png`;
      a.click();
      URL.revokeObjectURL(a.href);
    } catch { toast.error(T("Téléchargement impossible.")); }
  };

  return (
    <div data-testid="page-ramadan" className="max-w-6xl mx-auto px-4 sm:px-8 py-10">
      <div className="text-[11px] uppercase tracking-[0.22em] text-[color:var(--papicoo-accent)] inline-flex items-center gap-2">
        <Moon size={14} className="text-[color:var(--papicoo-gold)]" /> {t("ramadan.eyebrow")}
      </div>
      <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[color:var(--papicoo-ink)] mt-2 leading-[0.98]">
        {t("ramadan.title1")}<br />
        <span className="italic font-light">{t("ramadan.title2")}</span>
      </h1>
      <p className="text-[color:var(--papicoo-muted)] mt-3 max-w-2xl text-sm sm:text-base">
        {t("ramadan.intro")}
      </p>

      {currentDay && (
        <div data-testid="ramadan-today-pill" className="mt-4 inline-flex items-center gap-2 px-4 py-1.5 rounded-full bg-[color:var(--papicoo-gold)]/15 border border-[color:var(--papicoo-gold)]/40 text-xs uppercase tracking-[0.18em] text-[color:var(--papicoo-ink)]">
          <Calendar size={12} /> {t("ramadan.today", { day: currentDay })}
        </div>
      )}

      {loading ? (
        <div className="mt-10 text-center text-[color:var(--papicoo-muted)] italic">
          <Sparkles className="animate-pulse inline-block mr-2" /> {t("ramadan.loading")}
        </div>
      ) : (
        <div className="mt-8 grid lg:grid-cols-[1fr_1.1fr] gap-6 items-start">
          {/* COLONNE GAUCHE — calendrier 30 jours */}
          <div className="space-y-5">
            {DECADES.map((dec) => {
              const dayItems = days.filter((d) => d.day >= dec.range[0] && d.day <= dec.range[1]);
              return (
                <div key={dec.key} className={`papicoo-card p-4 bg-gradient-to-br ${dec.color}`}>
                  <div className="text-[10px] uppercase tracking-[0.18em] text-[color:var(--papicoo-muted)] mb-3">{t(`ramadan.${dec.key}`)}</div>
                  <div className="grid grid-cols-5 gap-2">
                    {dayItems.map((d) => {
                      const active = selected?.day === d.day;
                      const isToday = currentDay === d.day;
                      return (
                        <button
                          key={d.day}
                          data-testid={`ramadan-day-${d.day}`}
                          onClick={() => setSelected(d)}
                          className={`relative aspect-square rounded-lg flex flex-col items-center justify-center text-xs transition-all
                            ${active
                              ? "bg-[color:var(--papicoo-ink)] text-[color:var(--papicoo-sand)] shadow-lg scale-105"
                              : "bg-white/70 hover:bg-white text-[color:var(--papicoo-ink)] border border-[color:var(--papicoo-border)]"}
                          `}
                        >
                          <span className="text-[10px] opacity-60 leading-none">{t("ramadan.day").charAt(0)}</span>
                          <span className="font-display text-lg leading-tight">{d.day}</span>
                          {isToday && (
                            <span className="absolute -top-1 -right-1 w-2.5 h-2.5 rounded-full bg-[color:var(--papicoo-gold)] ring-2 ring-white" />
                          )}
                        </button>
                      );
                    })}
                  </div>
                </div>
              );
            })}
          </div>

          {/* COLONNE DROITE — détail jour + image + partage */}
          {selected && (
            <div className="space-y-5">
              <div className="papicoo-card overflow-hidden">
                <img
                  data-testid="ramadan-day-image"
                  src={imageUrl}
                  alt={`Ramadan jour ${selected.day}`}
                  className="w-full h-auto block"
                />
              </div>
              <div className="papicoo-card p-6">
                <div className="text-[11px] uppercase tracking-[0.18em] text-[color:var(--papicoo-accent)]">
                  Jour {selected.day} · {selected.theme}
                </div>
                <p data-testid="ramadan-verse-text" className="font-display text-xl sm:text-2xl text-[color:var(--papicoo-ink)] italic leading-snug mt-3">
                  « {T(selected.text)} »
                </p>
                <div className="mt-3 text-sm text-[color:var(--papicoo-gold)] font-medium">
                  Sourate {selected.sourate_num} · {selected.sourate_name_ar_translit}
                  <span className="text-[color:var(--papicoo-muted)] italic font-normal"> · {selected.sourate_name_fr}</span>
                  <span className="text-[color:var(--papicoo-muted)]"> · verset {selected.verset_num}</span>
                </div>

                <div className="mt-5 border-t border-[color:var(--papicoo-border)] pt-4 flex flex-wrap gap-2 items-center">
                  <button data-testid="ramadan-share-wa" onClick={() => share("whatsapp")} className="w-10 h-10 inline-flex items-center justify-center rounded-full bg-[#25D366] text-white hover:scale-105 transition" aria-label={T("WhatsApp")}><MessageCircle size={18} /></button>
                  <button data-testid="ramadan-share-fb" onClick={() => share("facebook")} className="w-10 h-10 inline-flex items-center justify-center rounded-full bg-[#1877F2] text-white hover:scale-105 transition" aria-label={T("Facebook")}><Facebook size={18} /></button>
                  <button data-testid="ramadan-share-tw" onClick={() => share("twitter")} className="w-10 h-10 inline-flex items-center justify-center rounded-full bg-[#0F0F0F] text-white hover:scale-105 transition" aria-label={T("Twitter / X")}><Twitter size={18} /></button>
                  <button data-testid="ramadan-share-copy" onClick={copyLink} className="w-10 h-10 inline-flex items-center justify-center rounded-full bg-[color:var(--papicoo-ink)] text-[color:var(--papicoo-sand)] hover:scale-105 transition" aria-label={T("Copier")}><Link2 size={18} /></button>
                  <button
                    data-testid="ramadan-download"
                    onClick={download}
                    className="ml-auto inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full papicoo-btn-gold text-sm"
                  >
                    <Download size={14} /> {t("ramadan.image")}
                  </button>
                </div>

                <button
                  data-testid="ramadan-listen"
                  onClick={async () => {
                    if (audioState === "playing") { audioRef.current?.pause(); setAudioState("idle"); return; }
                    setAudioState("loading");
                    try {
                      const res = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/ramadan/audio/${selected.day}`, { responseType: "blob", headers: adminHeaders() });
                      const url = URL.createObjectURL(res.data);
                      audioRef.current?.pause();
                      const a = new Audio(url);
                      audioRef.current = a;
                      a.onended = () => setAudioState("idle");
                      a.onerror = () => { setAudioState("idle"); toast.error(T("Lecture impossible.")); };
                      await a.play();
                      setAudioState("playing");
                    } catch (e) { setAudioState("idle"); if (e?.response?.status === 401) toast.info(T("La lecture vocale est réservée pour le moment. Le texte reste disponible.")); else toast.error(T("Lecture impossible.")); }
                  }}
                  className="mt-4 w-full inline-flex items-center justify-center gap-2 px-4 py-2.5 rounded-full bg-[color:var(--papicoo-ink)] text-[color:var(--papicoo-sand)] hover:bg-[color:var(--papicoo-accent)] transition text-sm"
                >
                  {audioState === "loading" ? <Sparkles size={14} className="animate-pulse" /> : audioState === "playing" ? <Pause size={14} /> : <Volume2 size={14} />}
                  {audioState === "loading" ? t("ramadan.preparing") : audioState === "playing" ? t("ramadan.listening") : t("ramadan.listen")}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}
